import { backendClient } from '../api/backendClient';
import { getSpotifyUserId } from '../auth/spotifyUserId';
import { getFromLocalStorageWithExpiry } from '../../utils/localstorage';
import { authService } from '../spotify/auth';
import type { QueueItem, QueueSessionEnvelope, QueueSessionSummary } from '../queue/types';
import { isVerboseLoggingEnabled } from '../debug/logging';

const LOG_NAMESPACE = '[SpotifyTokenTrace]';

const maskToken = (token: string | null | undefined): string => {
  if (typeof token !== 'string' || token.length === 0) return 'null';
  return token;
};

const isDevelopmentEnv =
  typeof process !== 'undefined' ? process.env.NODE_ENV !== 'production' : true;
const isDevelopment = isDevelopmentEnv && isVerboseLoggingEnabled();

const traceDebug = (message: string, details?: Record<string, unknown>) => {
  if (isDevelopment) {
    console.log(LOG_NAMESPACE, message, details ?? '');
  }
};

const traceWarn = (message: string, details?: Record<string, unknown>) => {
  console.warn(LOG_NAMESPACE, message, details ?? '');
};

export type QueueSessionStatus = QueueSessionSummary['status'];

export type QueueSeedDescriptor =
  | {
      type: 'liked-recordings';
      id: string;
      shuffle?: boolean;
      limit?: number;
    }
  | {
      type: 'album-recordings';
      albumId: string;
      shuffle?: boolean;
      limit?: number;
    }
  | {
      type: 'recording-selection';
      recordingIds: string[];
      shuffle?: boolean;
      limit?: number;
    }
  | {
      type: 'playlist-tracks';
      playlistId: string;
      shuffle?: boolean;
      limit?: number;
    }
  | {
      type: 'manual-tracks';
      trackUris: string[];
      shuffle?: boolean;
      limit?: number;
    }
  | {
      type: 'custom-mix';
      mixId: string;
      shuffle?: boolean;
      limit?: number;
      popularityBeta?: number;
      familiarPopularityBeta?: number;
    };

export type QueueActionType =
  | 'next-track'
  | 'previous-track'
  | 'skip-recording'
  | 'jump-to-index'
  | 'toggle-shuffle'
  | 'remove-items'
  | 'remove-group'
  | 'move-track'
  | 'move-group'
  | 'append-recordings'
  | 'append-tracks';

export type QueueActionOrigin = 'user' | 'system';

export type QueueActionRequest =
  | {
      action: Exclude<
        QueueActionType,
        'jump-to-index' | 'remove-items' | 'remove-group' | 'move-track' | 'move-group'
      >;
      revision: number;
      origin: QueueActionOrigin;
      deviceId?: string;
    }
  | {
      action: 'jump-to-index';
      revision: number;
      origin: QueueActionOrigin;
      targetIndex: number;
      deviceId?: string;
    }
  | {
      action: 'remove-items';
      revision: number;
      origin: QueueActionOrigin;
      targetItemIds: string[];
      deviceId?: string;
    }
  | {
      action: 'remove-group';
      revision: number;
      origin: QueueActionOrigin;
      targetGroupId: string;
      deviceId?: string;
    }
  | {
      action: 'move-track';
      revision: number;
      origin: QueueActionOrigin;
      groupId: string;
      sourceIndex: number;
      targetIndex: number;
      deviceId?: string;
    }
  | {
      action: 'move-group';
      revision: number;
      origin: QueueActionOrigin;
      groupId: string;
      targetGroupIndex: number;
      deviceId?: string;
    }
  | {
      action: 'append-recordings';
      revision: number;
      origin: QueueActionOrigin;
      recordingIds: string[];
      deviceId?: string;
    }
  | {
      action: 'append-tracks';
      revision: number;
      origin: QueueActionOrigin;
      trackUris: string[];
      trackDurationsMs?: Array<number | null>;
      trackMetadata?: Array<Record<string, unknown>>;
      deviceId?: string;
    };

export type CreateQueueSessionRequest = {
  deviceId: string;
  seed: QueueSeedDescriptor;
  shuffle?: boolean;
};

export type QueueProgressUpdatePayload = {
  currentItemId?: string | null;
  currentItemIndex?: number | null;
  currentProgressMs?: number | null;
  deviceId?: string | null;
  expectedRevision: number;
  reportedAt?: string | Date | number | null;
  resolvedItemId?: string | null;
  resolvedTrackUri?: string | null;
};

export interface BackendError extends Error {
  status?: number;
  payload?: unknown;
}

const resolveBackendUrl = (path: string, overrideBase?: string): string => {
  if (/^https?:\/\//i.test(path)) {
    return path;
  }

  const base = overrideBase ?? backendClient.defaults.baseURL;
  if (!base) {
    return path;
  }

  const trimmedBase = base.replace(/\/+$/, '');
  const normalizedPath = path.startsWith('/') ? path : `/${path}`;
  return `${trimmedBase}${normalizedPath}`;
};

const parseJson = (text: string): unknown => {
  if (!text) {
    return undefined;
  }

  try {
    return JSON.parse(text);
  } catch (error) {
    console.warn('[QueueAPI] Failed to parse backend response JSON', error);
    return undefined;
  }
};

const resolveSpotifyAccessToken = async (): Promise<string | null> => {
  const readFromStorage = (key: string): string | null => {
    const raw = getFromLocalStorageWithExpiry(key) as string | null;
    if (typeof raw === 'string') {
      const trimmed = raw.trim();
      if (trimmed.length > 0) {
        traceDebug('resolveSpotifyAccessToken storage hit', {
          key,
          token: maskToken(trimmed)
        });
        return trimmed;
      }
    }
    return null;
  };

  const stored = readFromStorage('access_token') ?? readFromStorage('spotify_access_token');
  if (stored) {
    return stored;
  }

  try {
    const fresh = await authService.getValidAccessToken();
    const token = typeof fresh === 'string' && fresh.trim().length > 0 ? fresh.trim() : null;
    traceDebug('resolveSpotifyAccessToken fetched', { token: maskToken(token) });
    return token;
  } catch (error) {
    traceWarn('resolveSpotifyAccessToken failed to refresh', {
      error: error instanceof Error ? error.message : String(error)
    });
    return null;
  }
};

type RequestOptions = RequestInit & {
  baseUrlOverride?: string;
};

async function backendRequest<T>(path: string, init?: RequestOptions): Promise<T> {
  if (typeof fetch === 'undefined') {
    throw new Error('fetch is not available in this environment; queue requests require the Fetch API.');
  }

  const { baseUrlOverride, ...requestInit } = init ?? {};
  const url = resolveBackendUrl(path, baseUrlOverride);
  const headers = new Headers(requestInit.headers ?? {});
  const method = (requestInit.method ?? 'GET').toUpperCase();

  if (method !== 'GET' && method !== 'HEAD' && requestInit.body && !headers.has('Content-Type')) {
    headers.set('Content-Type', 'application/json');
  }

  const spotifyUserId = getSpotifyUserId();
  if (spotifyUserId && !headers.has('X-User-Id')) {
    headers.set('X-User-Id', spotifyUserId);
  }

  if (!headers.has('X-Spotify-Access-Token')) {
    const token = await resolveSpotifyAccessToken();
    if (token) {
      if (typeof window !== 'undefined') {
        (window as any).__spotifyTokenTrace = token;
      }
      headers.set('X-Spotify-Access-Token', token);
      traceDebug('Attached Spotify access token header', {
        token: maskToken(token)
      });
    } else {
      traceWarn('No Spotify access token available for request', { path });
    }
  } else {
    traceDebug('Existing Spotify access token header retained', {
      token: maskToken(headers.get('X-Spotify-Access-Token')),
      path
    });
  }

  let refreshAttempted = false;

  while (true) {
    traceDebug('Issuing queue backend request', {
      path,
      method,
      hasSpotifyToken: headers.has('X-Spotify-Access-Token'),
      spotifyToken: maskToken(headers.get('X-Spotify-Access-Token')),
      refreshAttempted
    });

    const fetchStartTime = Date.now();
    const response = await fetch(url, {
      credentials: 'include',
      ...requestInit,
      headers
    });
    const fetchDuration = Date.now() - fetchStartTime;
    
    const parseStartTime = Date.now();
    const rawBody = await response.text();
    const payload = parseJson(rawBody);
    const parseDuration = Date.now() - parseStartTime;
    
    if (path === '/queue/session') {
      console.info('[DeviceInitTiming] Queue backend request fetch complete', {
        timestamp: new Date().toISOString(),
        fetchDurationMs: fetchDuration,
        parseDurationMs: parseDuration,
        status: response.status,
        ok: response.ok,
      });
    }

    if (response.ok) {
      return payload as T;
    }

    if (response.status === 401 && !refreshAttempted) {
      traceWarn('Queue backend request unauthorized; attempting Spotify token refresh', {
        path,
        method
      });
      try {
        const refreshedToken = await authService.refreshAccessToken();
        const normalizedToken =
          typeof refreshedToken === 'string' && refreshedToken.trim().length > 0
            ? refreshedToken.trim()
            : null;
        if (normalizedToken) {
          if (typeof window !== 'undefined') {
            (window as any).__spotifyTokenTrace = normalizedToken;
          }
          headers.set('X-Spotify-Access-Token', normalizedToken);
          traceDebug('Queue backend request retrying after token refresh', {
            path,
            method,
            token: maskToken(normalizedToken)
          });
          refreshAttempted = true;
          continue;
        }
        traceWarn('Queue backend request refresh yielded empty token', { path, method });
      } catch (refreshError) {
        traceWarn('Queue backend request token refresh failed', {
          path,
          method,
          error: refreshError instanceof Error ? refreshError.message : String(refreshError)
        });
      }
    }

    traceWarn('Queue backend request failed', {
      path,
      status: response.status,
      method,
      hasSpotifyToken: headers.has('X-Spotify-Access-Token'),
      refreshAttempted
    });
    const error: BackendError = new Error(`Backend request failed (${response.status})`);
    error.status = response.status;
    error.payload = payload;
    throw error;
  }
}

export const fetchQueueSession = async (): Promise<QueueSessionEnvelope> => {
  const startTime = Date.now();
  console.info('[DeviceInitTiming] fetchQueueSession API call start', {
    timestamp: new Date().toISOString(),
    path: '/queue/session',
  });
  try {
    const result = await backendRequest<QueueSessionEnvelope>('/queue/session');
    const duration = Date.now() - startTime;
    console.info('[DeviceInitTiming] fetchQueueSession API call complete', {
      timestamp: new Date().toISOString(),
      durationMs: duration,
    });
    return result;
  } catch (error) {
    const duration = Date.now() - startTime;
    console.error('[DeviceInitTiming] fetchQueueSession API call failed', {
      timestamp: new Date().toISOString(),
      durationMs: duration,
      error: error instanceof Error ? error.message : String(error),
    });
    throw error;
  }
};

export const createQueueSession = async (
  payload: CreateQueueSessionRequest
): Promise<QueueSessionEnvelope> => {
  return backendRequest<QueueSessionEnvelope>('/queue/session', {
    method: 'POST',
    body: JSON.stringify(payload)
  });
};

export const createCustomMixQueue = async (
  deviceId: string,
  mixId: string,
  options?: { limit?: number; shuffle?: boolean }
): Promise<QueueSessionEnvelope> => {
  return createQueueSession({
    deviceId,
    seed: {
      type: 'custom-mix',
      mixId,
      ...(typeof options?.limit === 'number' ? { limit: options.limit } : {})
    },
    shuffle: options?.shuffle
  });
};

export const postQueueAction = async (
  payload: QueueActionRequest
): Promise<QueueSessionEnvelope> => {
  return backendRequest<QueueSessionEnvelope>('/queue/session/actions', {
    method: 'POST',
    body: JSON.stringify(payload)
  });
};

export const postQueueProgress = async (
  payload: QueueProgressUpdatePayload,
  options?: { keepalive?: boolean }
): Promise<QueueSessionEnvelope> => {
  return backendRequest<QueueSessionEnvelope>('/queue/session/progress', {
    method: 'POST',
    body: JSON.stringify(payload),
    keepalive: options?.keepalive ?? false
  });
};

export type { QueueSessionEnvelope, QueueSessionSummary, QueueItem };
